import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const PASTELS = ["bg-pastel-violet", "bg-pastel-blue", "bg-pastel-yellow"];

const initials = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export const TeamMemberCard = ({ member, index = 0 }) => {
  const { lang } = useLang();
  return (
    <motion.article
      initial={{ y: 30, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.08 }}
      className="group"
      data-testid={`team-member-${member.id}`}
    >
      <Link
        to={`/${lang}/team/${member.id}`}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ser-purple"
        aria-label={member.name}
      >
        <div className="relative overflow-hidden aspect-[4/5] bg-pale-violet">
          {member.photo ? (
            <img
              src={member.photo}
              alt={member.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          ) : (
            <div className={`flex h-full w-full items-center justify-center ${PASTELS[index % PASTELS.length]}`} aria-hidden="true">
              <span className="font-display text-5xl text-navy/40">{initials(member.name)}</span>
            </div>
          )}
          <span className="absolute left-0 bottom-0 h-1 w-0 bg-ser-yellow transition-[width] duration-500 group-hover:w-full" aria-hidden="true" />
        </div>
        <h3 className="mt-5 font-display text-xl leading-snug text-navy group-hover:text-ser-purple transition-colors">{member.name}</h3>
        <p className="mt-1 text-xs uppercase tracking-[0.18em] text-ser-purple font-medium">{member.role[lang]}</p>
        {member.country && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-navy/50">
            <MapPin size={12} strokeWidth={1.5} aria-hidden="true" />
            {member.country[lang]}
          </p>
        )}
        <p className="mt-3 text-sm leading-relaxed text-navy/70">{member.bio[lang]}</p>
      </Link>
    </motion.article>
  );
};

export const TeamGrid = ({ members }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="team-grid">
    {members.map((m, i) => (
      <TeamMemberCard key={m.id} member={m} index={i} />
    ))}
  </div>
);
